/**
 * The Earthworks half of the quote screen (EW-2, fieldiq-api
 * GET /earthworks/quotes/:id/state): where the customer is with the emailed
 * link (Sent → Viewed → Accepted / Declined, who signed and when), a copy of
 * the link, and the DEPOSIT — recorded only. The office asks for an amount or
 * a % of the total, marks it received (day, method, reference), and can undo
 * either until Batch 6 carries it to an invoice. An accepted quote converts
 * to an invoice here too (EwQuoteInvoiceSection, EW-6).
 *
 * PAID BY CARD (Batch 4b). The customer can pay the deposit through the
 * tenant's own Stripe from the accept link; the webhook marks it received with
 * method 'stripe' and the payment_intent as reference. This panel says "Paid by
 * card (Stripe)" and offers no Undo for it (the API refuses: refund it in
 * Stripe). `depositCardFlags` lists card payments that arrived and were not
 * recorded, for the office to reconcile.
 *
 * THE API DECIDES. The deposit amount on a % basis is the API's rounding of
 * the quote total, never this screen's; every refusal is ewQuoteRefusal.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Loader2 } from 'lucide-react';
import { api } from '../../api/client';
import { unwrapEw } from '../../hooks/useEarthworks';
import { EW_CUSTOMER_STATUS_LABEL, EW_DEPOSIT_METHODS, EW_DEPOSIT_STRIPE_METHOD, ewQuoteRefusal } from '../../lib/ew-quotes';
import { fmtCurrency } from '../../lib/display-labels';
import { formatInstant } from '../../lib/calendar-date';
import type { EwQuoteState } from '../../types/earthworks';
import { EwQuoteInvoiceSection } from './EwQuoteInvoiceSection';

const INPUT = 'bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1.5 text-sm text-white';
const BTN = 'fi-action fi-action--standard fi-action--secondary disabled:opacity-50 disabled:cursor-not-allowed';
const BTN_PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:opacity-50 disabled:cursor-not-allowed';

export const ewQuoteStateKey = (quoteId: string) => ['earthworks', 'quote-state', quoteId] as const;

export function EwQuotePanel({
  quoteId,
  canEdit,
  timeZone,
  onRefused,
  onToast,
}: {
  quoteId: string;
  canEdit: boolean;
  timeZone: string | null;
  onRefused: (r: { code?: string; error: string }) => void;
  onToast: (msg: string) => void;
}) {
  const qc = useQueryClient();
  const state = useQuery({
    queryKey: ewQuoteStateKey(quoteId),
    queryFn: async () => unwrapEw<EwQuoteState>((await api.earthworks.quotes.state(quoteId)).data).data,
  });
  const s = state.data;
  const cur = s?.currency === 'CAD' || s?.currency === 'USD' ? s.currency : null;
  const money = (v: string | null | undefined) => (v == null ? '—' : fmtCurrency(Number(v), cur));
  const when = (v: string | null | undefined) => (v ? formatInstant(v, timeZone) ?? '—' : '—');

  const write = <V,>(fn: (v: V) => Promise<unknown>, done: string, fallback: string) => ({
    mutationFn: fn,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ewQuoteStateKey(quoteId) }); onToast(done); },
    onError: (err: unknown) => onRefused(ewQuoteRefusal(err, fallback)),
  });

  const [basis, setBasis] = useState<'amount' | 'percent'>('percent');
  const [figure, setFigure] = useState('');
  const [recvDay, setRecvDay] = useState('');
  const [method, setMethod] = useState('');
  const [reference, setReference] = useState('');

  const setDeposit = useMutation(write(
    () => api.earthworks.quotes.setDeposit(quoteId, basis === 'percent' ? { basis, percent: figure } : { basis, amount: figure }),
    'Deposit saved', 'Failed to save the deposit'));
  const removeDeposit = useMutation(write(() => api.earthworks.quotes.removeDeposit(quoteId), 'Deposit removed', 'Failed to remove the deposit'));
  const markReceived = useMutation(write(
    () => api.earthworks.quotes.markReceived(quoteId, {
      ...(recvDay ? { received_on: recvDay } : {}),
      ...(method ? { method } : {}),
      ...(reference.trim() ? { reference: reference.trim() } : {}),
    }),
    'Deposit marked received', 'Failed to mark the deposit received'));
  const undoReceived = useMutation(write(() => api.earthworks.quotes.undoReceived(quoteId), 'Deposit received undone', 'Failed to undo'));

  if (state.isLoading) return null;
  if (state.isError || !s) {
    return <div className="rounded-xl border border-fieldiq-border bg-fieldiq-card p-4 text-sm text-fieldiq-muted">The customer link and deposit could not be loaded.</div>;
  }

  const link = s.link;
  const dep = s.deposit;
  const closed = s.status === 'declined' || s.status === 'expired';
  const copy = async () => {
    if (!link?.url) return;
    try { await navigator.clipboard.writeText(link.url); onToast('Link copied'); } catch { onToast(link.url); }
  };

  return (
    <div className="rounded-xl border border-fieldiq-border bg-fieldiq-card p-4 space-y-4" data-testid="ew-quote-panel">
      <div>
        <h3 className="text-sm font-semibold text-white mb-2">Customer</h3>
        <p className="text-sm text-white" data-testid="ew-quote-customer-status">{EW_CUSTOMER_STATUS_LABEL[s.customerStatus] ?? s.customerStatus}</p>
        {link ? (
          <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-fieldiq-muted">
            <div>Sent to: <span className="text-white">{link.sentTo ?? '—'}</span> · {when(link.sentAt)}</div>
            <div>Opened: <span className="text-white">{link.firstViewedAt ? `${when(link.firstViewedAt)} (${link.viewCount}×)` : 'not yet'}</span></div>
            {link.decision && (
              <div className="sm:col-span-2">
                {link.decision === 'accepted' ? 'Accepted' : 'Declined'} by <span className="text-white">{link.signerName ?? '—'}{link.signerTitle ? `, ${link.signerTitle}` : ''}</span> · {when(link.decidedAt)}
                {link.hasSignature ? ' · signed' : ''}{link.declineReason ? ` — “${link.declineReason}”` : ''}
              </div>
            )}
            <div>Link expires: <span className="text-white">{when(link.expiresAt)}</span></div>
          </dl>
        ) : (
          <p className="mt-1 text-xs text-fieldiq-muted">Send the quote to email the customer a link to accept or decline it.</p>
        )}
        {link?.url && !link.decision && (
          <button type="button" onClick={copy} className={`${BTN} mt-2`} data-testid="ew-quote-copy-link"><Copy size={14} /> Copy customer link</button>
        )}
      </div>

      <div className="border-t border-fieldiq-border pt-3">
        <h3 className="text-sm font-semibold text-white mb-2">Deposit <span className="font-normal text-fieldiq-muted">(recorded only — credited on the first invoice)</span></h3>
        {dep ? (
          <div className="text-sm text-white space-y-1" data-testid="ew-quote-deposit">
            <p>{money(dep.amount)}{dep.basis === 'percent' && dep.percent ? ` (${Number(dep.percent)}% of ${money(s.total)})` : ''}</p>
            {dep.received && dep.receivedMethod === EW_DEPOSIT_STRIPE_METHOD ? (
              <p className="text-xs text-fieldiq-muted" data-testid="ew-quote-deposit-card">
                Paid by card (Stripe) on {when(dep.receivedAt)} · {money(dep.receivedAmount)}{dep.receivedReference ? ` · ref ${dep.receivedReference}` : ''}
                {dep.appliedInvoiceId ? ' · carried to an invoice' : ''}
              </p>
            ) : (
            <p className="text-xs text-fieldiq-muted">
              {dep.received
                ? `Received ${money(dep.receivedAmount)} · ${when(dep.receivedAt)}${dep.receivedMethod ? ` · ${EW_DEPOSIT_METHODS.find((m) => m.value === dep.receivedMethod)?.label ?? dep.receivedMethod}` : ''}${dep.receivedReference ? ` · ${dep.receivedReference}` : ''}`
                : 'Not received yet'}
              {dep.appliedInvoiceId ? ' · carried to an invoice' : ''}
            </p>
            )}
          </div>
        ) : (
          <p className="text-xs text-fieldiq-muted">No deposit asked for.</p>
        )}

        {canEdit && !closed && (!dep || !dep.received) && (
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="ew-quote-deposit-form">
            <select aria-label="Deposit basis" value={basis} onChange={(e) => setBasis(e.target.value as 'amount' | 'percent')} className={INPUT}>
              <option value="percent">% of total</option>
              <option value="amount">Amount</option>
            </select>
            <input aria-label={basis === 'percent' ? 'Deposit percent' : 'Deposit amount'} inputMode="decimal" value={figure}
                   onChange={(e) => setFigure(e.target.value)} placeholder={basis === 'percent' ? 'e.g. 25' : 'e.g. 5000'} className={`${INPUT} w-28`} />
            <button type="button" className={BTN_PRIMARY} disabled={setDeposit.isPending || !figure.trim()} onClick={() => setDeposit.mutate(undefined)}>
              {setDeposit.isPending && <Loader2 size={14} className="animate-spin" />}{dep ? 'Change deposit' : 'Ask for deposit'}
            </button>
            {dep && (
              <button type="button" className={BTN} disabled={removeDeposit.isPending} onClick={() => removeDeposit.mutate(undefined)}>Remove</button>
            )}
          </div>
        )}

        {canEdit && dep && !dep.received && (
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="ew-quote-received-form">
            <input type="date" aria-label="Deposit received on" value={recvDay} onChange={(e) => setRecvDay(e.target.value)} className={INPUT} />
            <select aria-label="Deposit payment method" value={method} onChange={(e) => setMethod(e.target.value)} className={INPUT}>
              <option value="">Method…</option>
              {EW_DEPOSIT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <input aria-label="Deposit reference" placeholder="Reference" value={reference} onChange={(e) => setReference(e.target.value)} className={`${INPUT} w-32`} />
            <button type="button" className={BTN_PRIMARY} disabled={markReceived.isPending} onClick={() => markReceived.mutate(undefined)}>
              {markReceived.isPending && <Loader2 size={14} className="animate-spin" />}Mark received
            </button>
          </div>
        )}

        {(s.depositCardFlags?.length ?? 0) > 0 && (
          <div className="mt-3 rounded-lg border border-amber-500/40 p-3 text-xs text-amber-200 space-y-1" data-testid="ew-quote-deposit-card-flags">
            <p className="font-semibold">Card payment not recorded — refund or reconcile it in Stripe</p>
            {s.depositCardFlags!.map((f, i) => (
              <p key={f.reference ?? i}>
                {when(f.at)} · {f.amount == null ? '—' : fmtCurrency(Number(f.amount), f.currency === 'CAD' || f.currency === 'USD' ? f.currency : cur)}
                {f.reference ? ` · ref ${f.reference}` : ''}{f.reason ? ` — ${f.reason}` : ''}
              </p>
            ))}
          </div>
        )}

        {canEdit && dep?.received && !dep.appliedInvoiceId && dep.receivedMethod !== EW_DEPOSIT_STRIPE_METHOD && (
          <button type="button" className={`${BTN} mt-3`} disabled={undoReceived.isPending} onClick={() => undoReceived.mutate(undefined)}>Undo received</button>
        )}
      </div>

      {/* EW-6: an accepted quote converts to an invoice, in full or as a progress draw. */}
      {s.status === 'accepted' && <EwQuoteInvoiceSection quoteId={quoteId} quoteNumber={s.quoteNumber} />}
    </div>
  );
}
